import { useTranslation } from 'react-i18next'

import { useMutateChoosePaymentPlugin } from '@entities/Checkout'

import { useErrorInfoModal } from '@features/modal'

import { PaymentPluginId, URL_VERIFY_PAYMENT } from '@shared/contants'

const HALYK_SCRIPT_URL = import.meta.env.VITE_HALYK_SCRIPT_URL

// Загрузка скрипта Halyk
const loadHalykScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Проверяем, загружен ли уже скрипт
    if (window.halyk) {
      resolve()
      return
    }

    const existingScript = document.querySelector(`script[src="${HALYK_SCRIPT_URL}"]`)
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve())
      return
    }

    const script = document.createElement('script')
    script.src = HALYK_SCRIPT_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Halyk script'))
    document.head.appendChild(script)
  })
}

export const useAddPayment = () => {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ choosePlugin, { loading } ] = useMutateChoosePaymentPlugin()

  const addPayment = async (
    paymentPluginId: PaymentPluginId,
    orderId?: string,
  ) => {
    try {
      // Verification URL will be created after we get operationId
      let verificationUrl: URL | null = null

      const { data } = await choosePlugin({
        variables: {
          paymentPluginId,
          order_id: orderId,
          source: 'client'
        },
      })

      const response = data?.choosePaymentPlugin
      if (!response?.type || !response?.payload) return

      const encodedPayload =
        response.payload[response.type as keyof typeof response.payload]
      if (!encodedPayload) return

      const payloadData = JSON.parse(encodedPayload)

      // Create verification URL with operationId in path
      if (response.operationId) {
        verificationUrl = new URL(
          `${URL_VERIFY_PAYMENT}/${response.operationId}`,
          window.location.origin,
        )
      }

      const paymentType = response.type

      // Универсальная обработка на основе type
      if (paymentType === 'redirect') {
        // Редирект на внешний URL (например, страница оплаты банка)
        if (payloadData.url) {
          window.location.href = payloadData.url
        }
        return {
          type: 'redirect',
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'qr') {
        // QR код - показываем на desktop, редиректим на мобильном
        return {
          type: 'qr',
          qrUrl: payloadData.url,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'controllable') {
        // Halyk epayment - загружаем скрипт и вызываем cardverification
        try {
          const { paymentFormSettings } = payloadData

          await loadHalykScript()

          if (window.halyk?.cardverification) {
            window.halyk.cardverification(paymentFormSettings)
          }
          else {
            throw new Error('Halyk cardverification not available')
          }
        }
        catch (err) {
          console.error('Failed to initialize Halyk payment:', err)
          throw err
        }

        return {
          type: 'controllable',
          payload: payloadData,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }
      else if (paymentType === 'embed') {
        // Embed фрейм
        return {
          type: 'embed',
          payload: payloadData,
          operationId: response.operationId,
          verificationUrl: verificationUrl?.toString(),
        }
      }

      // Fallback для неизвестных типов
      return {
        type: response.type,
        operationId: response.operationId,
        verificationUrl: verificationUrl?.toString(),
      }
    }
    catch (error) {
      console.error('Failed to add or process payment:', error)
      openErrorInfoModal({
        description: te('add_payment'),
      })
    }
  }

  return { addPayment, loading }
}
